import React from "react"
import { Flex, CtaCard, Button, IconTypes } from "@capioweb/ui-components"
import {
	Property,
	StringProperty,
	BooleanProperty,
	LinkProperty,
} from "../../@types/EpiFoundationSpa/Property"
import { BlockCommonLayout } from "../../utils/layout"
import { LocalesContext } from "../../context"
import { urlIsInternal } from "../../utils"
import {
	sendAppDownloadDataLayerPush,
	sendCtaClickLayerPush,
} from "../../utils/piwikPro"

interface CapioNexusCardBlockProps {
	heading: StringProperty
	pinned: BooleanProperty
	icon?: StringProperty
	description: StringProperty
	showCapioOnlineLink: BooleanProperty
	capioOnlineLinkText: StringProperty
	cta: Record<
		"desktopCTALink" | "appleStoreCTALink" | "googlePlayStoreCTALink",
		LinkProperty
	>
}

type CapioNexusCardContainerContentAreaProperty = Property<
	CapioNexusCardBlockProps[]
>

interface CapioNexusCardContainerBlockProps {
	heading: StringProperty
	content: CapioNexusCardContainerContentAreaProperty
}

const getFlexBasisByItemCount = (items) => {
	if (items.length === 3) return { l: "calc(33.33% - 12px)" }
	if (items.length === 4) return { xl: "calc(25% - 12px)" }
}

type LocalCtaButtonProps = Pick<CapioNexusCardBlockProps, "pinned" | "cta">

const LocalCtaButton: React.FC<LocalCtaButtonProps> = ({ pinned, cta }) => {
	if (urlIsInternal(cta.desktopCTALink.href)) {
		return (
			<Button.Link
				to={cta?.desktopCTALink?.href ?? "#"}
				variant={pinned.value ? "navy" : "ghost"}
				onClick={() => sendCtaClickLayerPush(cta.desktopCTALink.text)}
				target={cta?.desktopCTALink?.target}
			>
				{cta.desktopCTALink.text}
			</Button.Link>
		)
	} else {
		return (
			<Button.Anchor
				href={cta.desktopCTALink.href}
				target={cta.desktopCTALink.target}
				variant={pinned.value ? "navy" : "ghost"}
				onClick={() => sendCtaClickLayerPush(cta.desktopCTALink.text)}
			>
				{cta.desktopCTALink.text}
			</Button.Anchor>
		)
	}
}

const CapioNexusCardContainerBlock: React.FC<CapioNexusCardContainerBlockProps> =
	({ content, ...rest }) => {
		const { translate, translationKeys } = React.useContext(LocalesContext)
		return content?.value ? (
			<BlockCommonLayout {...rest}>
				<Flex
					w={1}
					flexWrap="wrap"
					justifyContent="space-between"
					alignContent="space-between"
					overflow="hidden"
					style={{ borderRadius: "24px" }}
				>
					{content.value.length > 0 &&
						content.value.map((c, i) => (
							<Flex
								key={i}
								flexBasis={{
									_: "100%",
									m: content.value.length > 1 ? "calc(50% - 12px)" : "100%",
									...getFlexBasisByItemCount(content.value),
								}}
								mb={{
									_: i + 1 === content.value.length ? 0 : 2,
									m: content.value.length > 2 && i < 2 ? 3 : 0,
									l: content.value.length > 3 && i < 2 ? 3 : 0,
									xl: 0,
								}}
							>
								<CtaCard
									icon={c.icon?.value ? (c.icon.value as IconTypes) : null}
								>
									<CtaCard.Title>{c.heading.value}</CtaCard.Title>
									<CtaCard.Preamble>{c.description.value}</CtaCard.Preamble>
									{c.cta?.desktopCTALink &&
										c.cta?.appleStoreCTALink &&
										c.cta?.googlePlayStoreCTALink && (
											<CtaCard.Cta>
												{c.showCapioOnlineLink.value === true ? (
													<>
														{/* Buttons - for screens < m */}
														<Flex
															display={{ m: "none" }}
															flexDirection="column"
														>
															<Button.Anchor
																onClick={() =>
																	sendAppDownloadDataLayerPush("ios")
																}
																href={c.cta.appleStoreCTALink.href}
																target={c.cta.appleStoreCTALink.target}
																variant={c.pinned.value ? "navy" : "ghost"}
																mb={1}
															>
																{translate(translationKeys.appStore)}
															</Button.Anchor>
															<Button.Anchor
																onClick={() =>
																	sendAppDownloadDataLayerPush("android")
																}
																href={c.cta.googlePlayStoreCTALink.href}
																target={c.cta.googlePlayStoreCTALink.target}
																variant={c.pinned.value ? "navy" : "ghost"}
																mb={1}
															>
																{translate(translationKeys.googlePlay)}
															</Button.Anchor>
														</Flex>
														{/* Buttons - for screens > m */}
														{c.cta.desktopCTALink.href && (
															<Flex display={{ _: "none", m: "flex" }}>
																<LocalCtaButton cta={c.cta} pinned={c.pinned} />
															</Flex>
														)}
													</>
												) : (
													c.cta.desktopCTALink.href && (
														<LocalCtaButton cta={c.cta} pinned={c.pinned} />
													)
												)}
											</CtaCard.Cta>
										)}
								</CtaCard>
							</Flex>
						))}
				</Flex>
			</BlockCommonLayout>
		) : null
	}

export { CapioNexusCardContainerBlock }
